import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useChat } from '../../../hooks/useChat';
import ProTipService from '../../../services/proTipService';
import toast from 'react-hot-toast';

const CATEGORY_CONTEXT = {
  general: {
    label: 'General',
    description: 'general best practices for musical theatre performers — covering mindset, professionalism, vocal health, and career development'
  },
  auditioning: {
    label: 'Auditioning',
    description: 'audition preparation and technique — including song selection, slate, cold reading, callbacks, and handling rejection professionally'
  },
  first_day: {
    label: 'First Day',
    description: 'the first day of rehearsal — including how to introduce yourself, what to bring, how to take direction, and building rapport with the cast and creative team'
  },
  during_the_run: {
    label: 'During the Run',
    description: 'maintaining peak performance during a show run — covering vocal stamina, staying fresh, performance consistency, and handling unexpected moments on stage'
  },
  between_shows: {
    label: 'Between Shows',
    description: 'the time between performances — including vocal rest, nutrition, physical recovery, mental reset, and staying warmed up'
  }
};

const AICoachingTipsModal = ({ isOpen, onClose, onTipsCreated, category, user, onError }) => {
  const [generatedTips, setGeneratedTips] = useState([]);
  const [selectedTips, setSelectedTips] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [rawResponse, setRawResponse] = useState('');

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    if (error) toast?.error(error?.message || 'AI generation failed');
  }, [error]);

  // Parse the AI response into structured tips
  useEffect(() => {
    if (response && !isLoading && hasGenerated) {
      setRawResponse(response);
      try {
        // Try to parse JSON from the response
        const jsonMatch = response?.match(/\[\s*\{[\s\S]*\}\s*\]/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch?.[0]);
          if (Array.isArray(parsed)) {
            setGeneratedTips(parsed);
            // Select all by default
            setSelectedTips(new Set(parsed.map((_, i) => i)));
          }
        }
      } catch (e) {
        toast?.error('Could not parse AI response. Please try again.');
      }
    }
  }, [response, isLoading, hasGenerated]);

  const handleGenerate = () => {
    const ctx = CATEGORY_CONTEXT?.[category?.id];
    if (!ctx) return;

    setGeneratedTips([]);
    setSelectedTips(new Set());
    setHasGenerated(true);

    const messages = [
      {
        role: 'system',
        content:
          'You are an expert musical theatre coach and industry professional with decades of experience. Generate practical, actionable coaching tips based on real-world best practices. Always respond with valid JSON only — no markdown, no explanation, just the JSON array.'
      },
      {
        role: 'user',
        content: `Generate exactly 5 professional coaching tips for ${ctx?.description}.

Return ONLY a valid JSON array with exactly 5 objects. Each object must have:
- "title": a short, punchy title (max 8 words)
- "content": detailed, actionable advice (2-4 sentences, max 300 characters)

Example format:
[{"title": "Warm Up Your Voice First", "content": "Always spend 10-15 minutes on vocal warm-ups before any rehearsal or performance. Start with lip trills and gentle sirens before moving to full range exercises."}]

Category: ${ctx?.label}
Context: ${ctx?.description}

Respond with ONLY the JSON array, nothing else.`
      }
    ];

    sendMessage(messages, { max_completion_tokens: 1200 });
  };

  const toggleTip = (index) => {
    setSelectedTips(prev => {
      const next = new Set(prev);
      if (next?.has(index)) {
        next?.delete(index);
      } else {
        next?.add(index);
      }
      return next;
    });
  };

  const handleEditTip = (index, field, value) => {
    setGeneratedTips(prev =>
      prev?.map((tip, i) => (i === index ? { ...tip, [field]: value } : tip))
    );
  };

  const handleSave = async () => {
    const tipsToSave = generatedTips?.filter((_, i) => selectedTips?.has(i));
    if (tipsToSave?.length === 0) {
      toast?.error('Please select at least one tip to save.');
      return;
    }

    try {
      setIsSaving(true);
      onError?.(null);

      for (const tip of tipsToSave) {
        const { error: createError } = await ProTipService?.createProTip({
          title: tip?.title?.trim(),
          content: tip?.content?.trim(),
          category: category?.id,
          created_by: user?.id
        });
        if (createError) {
          onError?.(`Failed to save tip "${tip?.title}": ${createError}`);
          return;
        }
      }

      toast?.success(`${tipsToSave?.length} tip${tipsToSave?.length > 1 ? 's' : ''} saved successfully!`);
      onTipsCreated?.();
    } catch (err) {
      onError?.('An unexpected error occurred while saving tips.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const ctx = CATEGORY_CONTEXT?.[category?.id];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Icon name="Sparkles" size={16} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold">AI Coaching Tips Generator</h3>
                <p className="text-violet-100 text-sm">{ctx?.label} category</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={onClose}
              iconName="X"
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Info Banner */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-5">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={18} className="text-violet-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-violet-800">
                OpenAI will generate <strong>5 coaching tips</strong> based on best practices for{' '}
                <strong>{ctx?.description}</strong>. Review, edit, and select which tips to save.
              </p>
            </div>
          </div>

          {/* Generate Button */}
          {!hasGenerated || generatedTips?.length === 0 ? (
            <div className="text-center py-8">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
                  <p className="text-gray-600 font-medium">Generating tips with AI...</p>
                  <p className="text-gray-400 text-sm">This may take a few seconds</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto">
                    <Icon name="Sparkles" size={28} className="text-violet-600" />
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium mb-1">Ready to generate tips</p>
                    <p className="text-gray-500 text-sm">Click below to have OpenAI create 5 coaching tips for the <strong>{ctx?.label}</strong> category</p>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleGenerate}
                    iconName="Sparkles"
                    iconPosition="left"
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
                  >
                    Generate Tips with AI
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">
                  {selectedTips?.size} of {generatedTips?.length} tips selected
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSelectedTips(new Set(generatedTips.map((_, i) => i)))}
                    className="text-xs text-violet-600 hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => setSelectedTips(new Set())}
                    className="text-xs text-gray-500 hover:underline"
                  >
                    Deselect All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={handleGenerate}
                    disabled={isLoading}
                    className="text-xs text-violet-600 hover:underline disabled:opacity-50"
                  >
                    Regenerate
                  </button>
                </div>
              </div>

              {generatedTips?.map((tip, index) => (
                <div
                  key={index}
                  className={`border-2 rounded-xl p-4 transition-colors duration-150 ${
                    selectedTips?.has(index)
                      ? 'border-violet-400 bg-violet-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <button
                      onClick={() => toggleTip(index)}
                      className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center transition-colors ${
                        selectedTips?.has(index)
                          ? 'bg-violet-600 border-violet-600'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {selectedTips?.has(index) && (
                        <Icon name="Check" size={12} className="text-white" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={tip?.title || ''}
                        onChange={e => handleEditTip(index, 'title', e?.target?.value)}
                        className="w-full font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-violet-300 focus:border-violet-500 focus:outline-none pb-0.5 mb-2 text-sm"
                        maxLength={100}
                      />
                      <textarea
                        value={tip?.content || ''}
                        onChange={e => handleEditTip(index, 'content', e?.target?.value)}
                        rows={3}
                        className="w-full text-sm text-gray-600 bg-transparent border border-transparent hover:border-violet-300 focus:border-violet-500 focus:outline-none rounded p-1 resize-none"
                        maxLength={1000}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {generatedTips?.length > 0 && (
          <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3 bg-gray-50">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={isSaving || selectedTips?.size === 0}
              iconName={isSaving ? 'Loader' : 'Save'}
              iconPosition="left"
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
            >
              {isSaving ? 'Saving...' : `Save ${selectedTips?.size} Tip${selectedTips?.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICoachingTipsModal;
